class PdbMixMatch extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || 'default';
    this.storageKey = `pdb-mm-selection-${this.sectionId}`;

    this.tabs = Array.from(this.querySelectorAll('[data-mm-tab]'));
    this.cards = Array.from(this.querySelectorAll('[data-mm-card]'));
    this.sticky = this.querySelector('[data-mm-sticky]');
    this.stickyItems = this.querySelector('[data-mm-sticky-items]');
    this.stickyCount = this.querySelector('[data-mm-count]');
    this.stickyTotal = this.querySelector('[data-mm-total]');
    this.stickyCta = this.querySelector('[data-mm-checkout]');
    this.stickyError = this.querySelector('[data-mm-error]');
    this.progress = this.querySelector('[data-mm-progress]');
    this.progressFill = this.querySelector('[data-mm-progress-fill]');
    this.progressMessage = this.querySelector('[data-mm-progress-message]');
    this.totalOriginal = this.querySelector('[data-mm-total-original]');

    this.nounSingular = this.stickyCount.dataset.mmNounSingular || 'porte-clé sélectionné';
    this.nounPlural = this.stickyCount.dataset.mmNounPlural || 'porte-clés sélectionnés';
    this.ctaLabelDefault = this.stickyCta.dataset.mmLabelDefault || 'Ajouter ma composition au panier';
    this.ctaLabelLoading = this.stickyCta.dataset.mmLabelLoading || 'Ajout en cours…';

    this.tiers = [1, 2, 3]
      .map((n) => ({
        qty: parseInt(this.dataset[`tier${n}Qty`], 10),
        percent: parseInt(this.dataset[`tier${n}Percent`], 10),
        code: this.dataset[`tier${n}Code`],
      }))
      .filter((tier) => tier.qty > 0 && tier.percent > 0 && tier.code)
      .sort((a, b) => a.qty - b.qty);

    this.selection = new Map();
    this.restoreSelection();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setActiveTab(tab));
    });

    this.cards.forEach((card) => {
      const addBtn = card.querySelector('[data-mm-add]');
      const incBtn = card.querySelector('[data-mm-increment]');
      const decBtn = card.querySelector('[data-mm-decrement]');

      if (addBtn) addBtn.addEventListener('click', () => this.changeQty(card, 1));
      if (incBtn) incBtn.addEventListener('click', () => this.changeQty(card, 1));
      if (decBtn) decBtn.addEventListener('click', () => this.changeQty(card, -1));
    });

    if (this.stickyCta) {
      this.stickyCta.addEventListener('click', () => this.addSelectionToCart());
    }

    this.renderAll();
  }

  setActiveTab(activeTab) {
    this.tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab === activeTab)));
    const category = activeTab.dataset.mmTab;
    this.cards.forEach((card) => {
      const cats = (card.dataset.mmCategories || '').split(' ');
      card.hidden = category !== 'tous' && !cats.includes(category);
    });
  }

  changeQty(card, delta) {
    const variantId = card.dataset.mmVariantId;
    const current = this.selection.get(variantId) || {
      qty: 0,
      title: card.dataset.mmTitle,
      price: parseFloat(card.dataset.mmPrice),
      priceFormatted: card.dataset.mmPriceFormatted,
      image: card.dataset.mmImage,
    };

    current.qty = Math.max(0, current.qty + delta);

    if (current.qty === 0) {
      this.selection.delete(variantId);
    } else {
      this.selection.set(variantId, current);
    }

    this.persistSelection();
    this.updateCard(card, current.qty);
    this.renderSticky();
  }

  updateCard(card, qty) {
    const addBtn = card.querySelector('[data-mm-add]');
    const stepper = card.querySelector('[data-mm-stepper]');
    const qtyEl = card.querySelector('[data-mm-qty]');

    card.dataset.mmSelected = String(qty > 0);

    if (qty > 0) {
      if (addBtn) addBtn.hidden = true;
      if (stepper) stepper.hidden = false;
      if (qtyEl) qtyEl.textContent = String(qty);
    } else {
      if (addBtn) addBtn.hidden = false;
      if (stepper) stepper.hidden = true;
    }
  }

  renderAll() {
    this.cards.forEach((card) => {
      const entry = this.selection.get(card.dataset.mmVariantId);
      this.updateCard(card, entry ? entry.qty : 0);
    });
    this.renderSticky();
  }

  getActiveTier(totalQty) {
    let active = null;
    for (const tier of this.tiers) {
      if (totalQty >= tier.qty) active = tier;
    }
    return active;
  }

  getNextTier(totalQty) {
    return this.tiers.find((tier) => totalQty < tier.qty) || null;
  }

  formatCurrency(amount) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  }

  renderProgress(totalQty) {
    if (!this.progress || this.tiers.length === 0) return;

    const maxQty = this.tiers[this.tiers.length - 1].qty;
    const fillPercent = Math.min(100, (totalQty / maxQty) * 100);
    this.progressFill.style.transform = `scaleX(${fillPercent / 100})`;

    const activeTier = this.getActiveTier(totalQty);
    const nextTier = this.getNextTier(totalQty);

    if (!activeTier && nextTier) {
      this.progressMessage.textContent = `Encore ${nextTier.qty - totalQty} pour -${nextTier.percent}% !`;
    } else if (activeTier && nextTier) {
      this.progressMessage.textContent = `-${activeTier.percent}% débloqué · encore ${nextTier.qty - totalQty} pour -${nextTier.percent}% !`;
    } else if (activeTier) {
      this.progressMessage.textContent = `🎉 -${activeTier.percent}% débloqué, la réduction maximum !`;
    } else {
      this.progressMessage.textContent = '';
    }
  }

  renderSticky() {
    const entries = Array.from(this.selection.entries());
    const totalQty = entries.reduce((sum, [, item]) => sum + item.qty, 0);

    if (totalQty === 0) {
      this.sticky.hidden = true;
      return;
    }

    this.sticky.hidden = false;
    this.stickyError.hidden = true;

    this.stickyItems.innerHTML = '';
    entries.forEach(([variantId, item]) => {
      const thumb = document.createElement('div');
      thumb.className = 'pdb-mm__sticky-thumb';
      thumb.innerHTML = `
        ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="44" height="44">` : ''}
        <span class="pdb-mm__sticky-thumb-qty">${item.qty}</span>
        <button type="button" class="pdb-mm__sticky-thumb-remove" aria-label="Retirer ${item.title}">×</button>
      `;
      thumb.querySelector('.pdb-mm__sticky-thumb-remove').addEventListener('click', () => {
        this.selection.delete(variantId);
        this.persistSelection();
        const card = this.cards.find((c) => c.dataset.mmVariantId === variantId);
        if (card) this.updateCard(card, 0);
        this.renderSticky();
      });
      this.stickyItems.appendChild(thumb);
    });

    const total = entries.reduce((sum, [, item]) => sum + item.price * item.qty, 0);
    const activeTier = this.getActiveTier(totalQty);

    this.stickyCount.textContent = `${totalQty} ${totalQty > 1 ? this.nounPlural : this.nounSingular}`;

    if (activeTier) {
      const discounted = total * (1 - activeTier.percent / 100);
      this.totalOriginal.textContent = this.formatCurrency(total);
      this.totalOriginal.hidden = false;
      this.stickyTotal.textContent = this.formatCurrency(discounted);
    } else {
      this.totalOriginal.hidden = true;
      this.stickyTotal.textContent = this.formatCurrency(total);
    }

    this.renderProgress(totalQty);
  }

  persistSelection() {
    try {
      const plain = {};
      this.selection.forEach((item, variantId) => {
        plain[variantId] = item.qty;
      });
      sessionStorage.setItem(this.storageKey, JSON.stringify(plain));
    } catch (e) {
      /* sessionStorage unavailable: selection just won't survive a reload */
    }
  }

  restoreSelection() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      const plain = JSON.parse(raw);
      Object.entries(plain).forEach(([variantId, qty]) => {
        const card = this.cards.find((c) => c.dataset.mmVariantId === variantId);
        if (!card || qty <= 0) return;
        this.selection.set(variantId, {
          qty,
          title: card.dataset.mmTitle,
          price: parseFloat(card.dataset.mmPrice),
          priceFormatted: card.dataset.mmPriceFormatted,
          image: card.dataset.mmImage,
        });
      });
    } catch (e) {
      /* corrupted or unavailable storage: start from an empty selection */
    }
  }

  async addSelectionToCart() {
    if (this.selection.size === 0) return;

    const items = Array.from(this.selection.entries()).map(([variantId, item]) => ({
      id: variantId,
      quantity: item.qty,
    }));
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
    const activeTier = this.getActiveTier(totalQty);

    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    this.stickyCta.disabled = true;
    this.stickyCta.textContent = this.ctaLabelLoading;
    this.stickyError.hidden = true;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Une erreur est survenue.');
      }

      this.selection.clear();
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        /* noop */
      }

      if (activeTier) {
        window.location.assign(`/discount/${encodeURIComponent(activeTier.code)}?redirect=/cart`);
      } else {
        window.location.assign('/cart');
      }
    } catch (error) {
      this.stickyError.textContent = `Impossible d'ajouter la composition au panier : ${error.message}`;
      this.stickyError.hidden = false;
      this.stickyCta.disabled = false;
      this.stickyCta.textContent = this.ctaLabelDefault;
    }
  }
}

if (!customElements.get('pdb-mix-match')) {
  customElements.define('pdb-mix-match', PdbMixMatch);
}
